/* global rwgps, google */
/** ********* IMPORTANT ***************
 * To use this component, you MUST be *
 * on a ridewithgps.com page with the *
 * following modules loaded:          *
 * app/assets/javascripts/base.js     *
 * app/assets/javascripts/js_maps.js  *
 * google_map_script_tag              *
 # webpack_manifest_script            *
 * babel_polyfill_script              *
 * webpack_bundle_tag 'common'        *
 * webpack_bundle_tag 'map_and_graph' *
 *************************************/
import React from 'react'
import { findDOMNode } from 'react-dom'
import styles from './GoogleMapEngine.scss'
import { MapTypeContext } from 'common/Map/MapTypeContext'
import { Viewport } from 'data/records'
import { tileServices } from 'lib/tileServices'
import { RenderBigMenuItem } from 'common/GlobalNav/AdminMenu/Items'

// nasty semiglobals
let GMAPS_LOAD_PENDING = false
let GMAPS_LOAD_CALLBACKS = []

const googleFormatBounds = bounds => ({
  east: bounds[1].lng,
  north: bounds[1].lat,
  south: bounds[0].lat,
  west: bounds[0].lng
})

const getGoogTileObject = overlay => {
  switch (overlay.type) {
    case 'osm':
      return new google.maps.ImageMapType({
        getTileUrl: function (coord, zoom) {
          const str = overlay.url + zoom + '/' + coord.x + '/' + coord.y + '.png'
          const withAPIKey = overlay.key
            ? `${str}?apikey=${overlay.key}`
            : str
          const sub = 'abc'[Math.floor(Math.random() * 3)]
          return withAPIKey.replace('{s}', sub)
        },
        tileSize: new google.maps.Size(256, 256),
        isPng: true,
        alt: overlay.alt,
        name: overlay.name,
        maxZoom: overlay.maxZoom,
        minZoom: overlay.minZoom
      })
    case 'arcgis':
      return new google.maps.ImageMapType({
        getTileUrl: function (coord, zoom) {
          var sub = Math.floor(Math.random() * 4 + 1)
          var base = overlay.url.replace(/%{num}/, sub)
          return base + zoom + '/' + coord.y + '/' + coord.x + '?token=' + (overlay.token || '')
        },
        tileSize: new google.maps.Size(256, 256),
        isPng: true,
        alt: overlay.alt,
        name: overlay.name,
        maxZoom: overlay.maxZoom,
        minZoom: overlay.minZoom
      })
    case 'wms':
      // we no longer support any WMS tileservers, I've converted everything
      // that previously was WMS to the arcgis convention
      return null
  }
}

export const latLngToPixelCoordinate = (googleLatLng, mapInstance) => {
  const scale = Math.pow(2, mapInstance.getZoom())
  const proj = mapInstance.getProjection()
  const bounds = mapInstance.getBounds()

  const nw = proj.fromLatLngToPoint(
    new google.maps.LatLng(
      bounds.getNorthEast().lat(),
      bounds.getSouthWest().lng()
    )
  )
  const point = proj.fromLatLngToPoint(googleLatLng)

  const x = Math.floor((point.x - nw.x) * scale)
  const y = Math.floor((point.y - nw.y) * scale)

  return { x, y }
}

export class GoogleMapEngine extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      initialized: false,
      mapContext: {
        mapType: 'google',
        mapInstance: null,
        mapEngine: this
      }
    }
  }

  componentDidMount () {
    if (this.initialized()) {
      this.initMap()
    } else if (GMAPS_LOAD_PENDING) {
      GMAPS_LOAD_CALLBACKS.push(this.initMap)
    } else {
      this.lazyLoadGoogle()
    }
  }

  componentDidUpdate (prevProps, prevState) {
    const { overlay, scrollWheelZoom, showBikePaths, showHeatLayer } = this.props
    if (prevProps.overlay !== overlay) {
      this._map && this._map.setMapTypeId(this.props.overlay.id)
    }
    if (prevProps.scrollWheelZoom !== scrollWheelZoom) {
      this._map && this._map.set('scrollwheel', scrollWheelZoom)
    }
    if (prevProps.showBikePaths !== showBikePaths) {
      this._bikeLayer.setMap(showBikePaths ? this._map : null)
    }
    if (prevProps.showHeatLayer !== showHeatLayer) {
      this._map.overlayMapTypes.setAt(0, showHeatLayer ? this._heatLayer : null)
    }
  }

  componentWillUnmount () {
    if (this._map) {
      // if we got as far as booting up the map before unmounting, kill the event
      // listeners
      window.google.maps.event.removeListener(this._mapClickListenerHandle)
      window.google.maps.event.removeListener(this._mapBoundsChangedListenerHandle)
    }
  }

  lazyLoadGoogle () {
    GMAPS_LOAD_PENDING = true
    console.log('Starting Google Maps API lazy load')
    const script = document.createElement('script')
    const apiKey = rwgps.config.gmapsApiKey
    const version = '3.37'
    const mapLanguage = rwgps.config.gmapsMapLanguage
    const mapRegion = ''
    const src = '//maps.google.com/maps/api/js' +
      '?' + apiKey +
      '&v=' + version +
      '&language=' + mapLanguage +
      '&region=' + mapRegion
    window.onLazyLoadGoogleComplete = () => {
      console.log('Google Maps lazy load completed, running callbacks')
      // clean up
      delete window.onLazyLoadGoogleComplete
      GMAPS_LOAD_PENDING = false
      GMAPS_LOAD_CALLBACKS.forEach(cb => cb())
      GMAPS_LOAD_CALLBACKS = []
      this.onLazyLoadGoogleComplete()
    }
    script.setAttribute('src', src)
    script.setAttribute('onload', 'window.onLazyLoadGoogleComplete()')
    document.body.appendChild(script)
  }

  initialized = () => window.google && window.google.maps

  onLazyLoadGoogleComplete = () => {
    // bail out if unmounted
    if (!findDOMNode(this)) {
      return
    }
    // BOOM WORKING EXCEPT FOR UnauthorizedURLForClientIdMapError
    this.setState({ gmapsLoaded: true }, this.initMap)
  }

  // BIG change - we no longer are instantiating a `rwgps.Map` here, we are
  // directly booting up a google.maps.Map
  initMap = () => {
    const { initialViewport, overlay, scrollWheelZoom, showHeatLayer } = this.props
    const center = Viewport.getCenter(initialViewport)
    // default zoom of 10 will be immediately clobbered by the fitbounds in the
    // event that our initialViewport is a {bounds} instead of a {center, zoom}
    const zoom = initialViewport.zoom || 10
    this._map = new google.maps.Map(findDOMNode(this), {
      mapTypeId: overlay.id,
      center,
      zoom: zoom,
      disableDefaultUI: true,
      zoomControl: true,
      scrollwheel: scrollWheelZoom,
      streetViewControl: true,
      controlSize: 25,
      streetView: new google.maps.StreetViewPanorama(findDOMNode(this), {
        addressControlOptions: { position: google.maps.ControlPosition.BOTTOM_CENTER },
        enableCloseButton: true,
        visible: false
      }),
      styles: [{
        featureType: 'poi.business',
        elementType: 'labels',
        stylers: [
          { visibility: 'on' }
        ]
      }]
    })

    this.registerCustomMapTypes()

    this._bikeLayer = new window.google.maps.BicyclingLayer()

    this._heatLayer = new google.maps.ImageMapType({
      getTileUrl: (coord, zoom) => (
        '//heatmap.ridewithgps.com/normalized/' + zoom + '/' + coord.x + '/' + coord.y + '.png?start_color=%23bcebf0&end_color=%23e94b65'
      ),
      tileSize: new google.maps.Size(256, 256),
      isPng: true,
      name: 'Heatmap',
      minZoom: 3,
      maxZoom: 15,
      opacity: 0.7
    })

    if (showHeatLayer) {
      this._map.overlayMapTypes.push(this._heatLayer)
    }

    this.setState({
      initialized: true,
      mapContext: {
        mapType: 'google',
        mapInstance: this._map,
        mapEngine: this
      }
    })
    initialViewport.bounds && this._map.fitBounds(googleFormatBounds(initialViewport.bounds))
    // map init is done, let's boot up some event listeners
    this._mapBoundsChangedListenerHandle = this._map.addListener('bounds_changed', this.onBoundsChanged)
    this._mapClickListenerHandle = this._map.addListener('click', this.props.onMapClick)
  }

  registerCustomMapTypes = () => {
    Object.values(tileServices).forEach(overlay => {
      // no need to add builtin types
      if (overlay.type === 'google') { return }
      const customGoogleTiles = getGoogTileObject(overlay)
      this._map.mapTypes.set(overlay.id, customGoogleTiles)
    })
  }

  onBoundsChanged = () => {
    if (this.props.onViewportChanged) {
      const _center = this._map.getCenter()
      const center = { lat: _center.lat(), lng: _center.lng() }
      const zoom = this._map.getZoom()
      this.props.onViewportChanged(Viewport({ center, zoom }))
    }
  }

  jumpTo = (viewport) => {
    if (viewport.bounds) {
      this._map.fitBounds(googleFormatBounds(viewport.bounds))
      return
    }

    if (viewport.center && viewport.zoom) {
      this._map.setZoom(viewport.zoom)
      this._map.setCenter(viewport.center)
    }
  }

  flyTo = (viewport, options = {}) => {
    const padding = options.padding || { top: 80, right: 80, bottom: 80, left: 80 }
    if (viewport.bounds) {
      this._map.fitBounds(googleFormatBounds(viewport.bounds), padding)
      return
    }

    if (viewport.center && viewport.zoom) {
      this._map.setZoom(viewport.zoom) // there's no native way to animate zoom in gmaps
      this._map.panTo(viewport.center)
    }
  }

  // goog doesn't use a canvas, so this happens for free
  resizeToParentHeight = () => {}

  // BIG GOTCHA - gmaps latLngToPoint actually return pixel coords. In a massive WTF,
  // the google map system doesn't expose this functionality. We can translate
  // to their "Point plane", which has a *relationship* with the pixel
  // coordinate space, but doesn't quite match. This means things like our
  // 50px distance threshold for point snapping won't translate 1:1 to the
  // gmaps space until fixed.
  //
  // Instead, we have to do a bunch of manual projection work.
  // See this StackOverflow post for more info:
  //
  // https://stackoverflow.com/questions/1538681/how-to-call-fromlatlngtodivpixel-in-google-maps-api-v3/8240226
  latLngToPixelCoordinate = (latLng) => {
    // this is one of those methods that won't take a literal, so we have
    // to convert latLng to a goog latLng
    const ll = new google.maps.LatLng(latLng.lat, latLng.lng)

    return latLngToPixelCoordinate(ll, this._map)
  }

  render () {
    const { mapContext } = this.state
    const { children } = this.props
    return (
      <div className={styles.GoogleMapEngine} style={{ height: '100%', width: '100%' }}>
        <RenderBigMenuItem getEast={() => this._map.getBounds().getNorthEast().lng()}
          getWest={() => this._map.getBounds().getSouthWest().lng()}
          getNorth={() => this._map.getBounds().getNorthEast().lat()}
          getSouth={() => this._map.getBounds().getSouthWest().lat()} />
        <MapTypeContext.Provider value={mapContext}>
          {/* wait to init children until mapContext is populated */}
          {mapContext.mapInstance && children}
        </MapTypeContext.Provider>
      </div>
    )
  }
}
